import React from "react";
import A4PageFrame from "./A4PageFrame";
import { ROWS_PER_PAGE_PORTRAIT, ROW_HEIGHT } from "./pageMetrics";
import { fmtNum, fmtPct } from "./formatters";

// Phase 3 (B4a): Compteurs (Meters) — mirrors PDF pages 14–16.
//
// Per-meter row: Numéro, Désignation, Energie, Compteur, current value, Unit,
// prior value, Diff., %, Info. Per client direction (Q2): no anomaly flagging —
// the raw % is shown even when extreme (we saw +14627% etc. in Etang B). Meters
// are NOT subtotalled (each meter is independent).
//
// Pagination: ~46 rows per A4 page. The final page also carries the two charts.
//
// Props
//   data            response from GET /v1/repartition-report/meters
//   periodYears     { current, prior? } — chart legend
//   compareEnabled  bool

// The pinned `lineHeight` on the `td` style below keeps every row a
// deterministic 19 px, which is what actually prevents the trailing rows from
// being cropped out of the html-to-image capture between pages. The row COUNT is
// derived from the calibrated portrait page geometry (see pageMetrics.js) —
// 46 rows, a one-row cushion below the 48-row count that previously clipped the
// 71-meter Plaines 6 report — instead of a hand-tuned magic number. Single
// source of truth shared with the METERS_ROWS_PER_PAGE mirror in Preview.js, so
// the "Page X/N" footer can never drift from the actual page break.
const ROWS_PER_PAGE = ROWS_PER_PAGE_PORTRAIT;

const MetersComparison = ({
  data,
  building,
  periodLabel,
  periodYears,
  compareEnabled,
  whiteLabelling,
  logoUrl,
  basePageNumber = 1,
  grandTotalPages = 1,
}) => {
  const meters = data?.meters || [];
  const currentYear = periodYears?.current ?? "Current";
  const priorYear = periodYears?.prior ?? "Prior";

  const pages = [];
  for (let i = 0; i < meters.length; i += ROWS_PER_PAGE) {
    pages.push(meters.slice(i, i + ROWS_PER_PAGE));
  }
  if (pages.length === 0) pages.push([]);

  // Bar chart: aggregate by energy bucket (Electricité, Eau froide, Chaud, ECS,
  // Pass #3: the client asked to DELETE the Compteurs charts. This section is now
  // table pages ONLY (no trailing "Synthèse" page). metersPages in Preview.js must
  // match (no +1).
  const totalSectionPages = pages.length;

  return (
    <>
      {pages.map((rows, pageIdx) => {
        const pageKey = rows[0]?.consumption ?? `empty-${pageIdx}`;
        return (
          <A4PageFrame
            key={`m-${pageKey}`}
            building={building}
            periodLabel={periodLabel}
            sectionTitle={`Compteurs (${pageIdx + 1}/${totalSectionPages})`}
            whiteLabelling={whiteLabelling}
            logoUrl={logoUrl}
            pageNumber={basePageNumber + pageIdx}
            totalPages={grandTotalPages}
          >
            <table style={tbl}>
              {/* Client feedback 2026-08-31 (Confédération Centre, page 3 of
                  Compteurs): "values goes out of the page … You can limit the
                  collumn Désignation even if some data will be hyde so all pages
                  got the same structure".
                  Cause: the table had `whiteSpace: nowrap` + `overflow: hidden`
                  on the cells but NO tableLayout:"fixed" and no colgroup. Under
                  auto layout a td's overflow is ignored for sizing, so each PAGE
                  sized its own columns to its own longest Désignation — page 3
                  carries the 70-character names ("C103 - Secteur chaud Commerces
                  Monobloc Penta Restaurant Lipp Statique") and stretched the
                  table past the 730 px portrait content band (794 − 2×32 padding).
                  That is also why only some pages broke: every page is its own
                  <table>.
                  Fixed widths below total 723 px (compare) / 726 px (no compare),
                  so Désignation now ellipsizes instead of pushing the sheet, and
                  every page has identical columns. Same pattern as CostDetails
                  and ResultsList. */}
              {/* Widths are ordered by what may NOT be lost. Every column that can
                  carry a large figure keeps ~77-80 px, enough for the widest value
                  in this data ("8'704'472.60", 12 chars at fontSize 10) — a cut
                  number is wrong information, whereas a cut name is not, and the
                  client explicitly accepted "even if some data will be hyde".
                  Numéro is 50 px so its own header fits (see the `th` note below).
                  Désignation therefore lands at ~120 px in compare mode, which shows
                  roughly the first 24 characters (see the % note below); in the
                  7-column layout it gets
                  292 px and shows ~85% of the longest name. Verified in headless
                  Chrome: table scrollWidth 730 px = content band, no overflow. */}
              {/* Client 2026-10-15 (Plaines 6 Compteurs): "Collumn % of consumptions
                  should be visible (we can probably reduce other collumn …)". The %
                  column was 40 px — 32 px of text room — so "-100.00", "1'146.03" and
                  even "-19.86" were cut to "-100…" on the client's screen. Widest real
                  value across every building is "12'840.54" (Plaines 6 22/23, meter
                  333) at ~52 px; 68 px leaves ~8 px of margin for it. The 28 px come
                  out of Désignation (148 → 120), the only column whose loss the
                  client has already accepted; font size unchanged. Measured with PP
                  Neue Montreal on all 17 local periods, both on screen (tabular digits)
                  and through the html-to-image PDF capture (whose `font` shorthand
                  resets the digits to proportional, so it is the narrower of the two). */}
              <colgroup>
                <col style={{ width: compareEnabled ? 50 : 50 }} />
                <col style={{ width: compareEnabled ? 120 : 292 }} />
                <col style={{ width: compareEnabled ? 64 : 72 }} />
                <col style={{ width: compareEnabled ? 64 : 84 }} />
                <col style={{ width: compareEnabled ? 77 : 88 }} />
                <col style={{ width: compareEnabled ? 80 : 92 }} />
                <col style={{ width: compareEnabled ? 44 : 48 }} />
                {compareEnabled && <col style={{ width: 80 }} />}
                {compareEnabled && <col style={{ width: 76 }} />}
                {compareEnabled && <col style={{ width: 68 }} />}
              </colgroup>
              <thead>
                {/* M1.3: column structure aligned to the standardised report
                  layout confirmed by the client on 10.06.2026. Changes:
                    - Reading column header: "31.12.YYYY" → "Lecture"
                      (semantically the prior-period end reading)
                    - Unité moved from position 6 to position 7 (after the
                      current-year value column)
                    - "Diff." header → "Différence"
                    - "Energie" → "Énergie" (accent restored) */}
                <tr style={{ borderBottom: "1px solid #00033F" }}>
                  <th style={th}>Numéro</th>
                  <th style={th}>Désignation</th>
                  <th style={th}>Énergie</th>
                  <th style={th}>Compteur</th>
                  <th style={thNum}>Lecture</th>
                  <th style={thNum}>{currentYear}</th>
                  <th style={th}>Unité</th>
                  {compareEnabled && <th style={thNum}>{priorYear}</th>}
                  {compareEnabled && <th style={thNum}>Différence</th>}
                  {compareEnabled && <th style={thNum}>%</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.consumption}>
                    <td style={td}>{m.consumption}</td>
                    <td style={td}>{m.name || ""}</td>
                    <td style={td}>{m.energie || ""}</td>
                    <td style={td}>{m.compteur || ""}</td>
                    <td style={tdNum}>{m.current_reading != null ? fmtNum(m.current_reading) : ""}</td>
                    <td style={tdNum}>{fmtNum(m.main_value)}</td>
                    <td style={td}>{m.unit || ""}</td>
                    {compareEnabled && <td style={tdNum}>{fmtNum(m.compare_value)}</td>}
                    {compareEnabled && <td style={tdNum}>{fmtNum(m.diff)}</td>}
                    {compareEnabled && <td style={tdNum}>{fmtPct(m.diff_pct)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </A4PageFrame>
        );
      })}
    </>
  );
};

// marginBottom:0 overrides Bootstrap Reboot's `table{margin-bottom:1rem}` so the
// fixed content region keeps its full height (see pageMetrics.js).
// tableLayout:"fixed" is what makes the <colgroup> widths above authoritative —
// without it the browser re-sizes columns to fit the content and the nowrap
// Désignation runs off the page (see the note in the JSX).
const tbl = { width: "100%", borderCollapse: "collapse", marginTop: 4, marginBottom: 0, tableLayout: "fixed" };
// M1.1: bumped from 9/8.5pt to 11/10pt per client feedback.
// nowrap + overflow:hidden mirrors `td` below. Without it a header wider than its
// fixed column bled into the next one — "Numéro" at 42 px painted straight into
// "Désignation" with no gap (caught in a headless-Chrome screenshot, invisible to
// jsdom which has no layout engine). Headers are now clipped like data, so a column
// can never be visually corrupted by a long label again.
const th = {
  boxSizing: "border-box",
  padding: "3px 4px",
  fontWeight: 600,
  textAlign: "left",
  fontSize: 11,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const thNum = { ...th, textAlign: "right" };
// Plaines-6 fix: pin lineHeight so row height is deterministic (regardless of PP
// Neue Montreal metrics) — this is what actually prevents the trailing rows from
// being clipped out of the html-to-image capture between pages.
const td = {
  // Row height PINNED to ROW_HEIGHT.portrait so no meter row drifts taller and
  // gets clipped at a page boundary (recurring clipping bug on the 71-meter
  // Plaines 6 report). nowrap + overflow:hidden keeps long names single-line.
  boxSizing: "border-box",
  height: ROW_HEIGHT.portrait,
  padding: "2px 4px",
  fontSize: 10,
  lineHeight: "14px",
  borderBottom: "1px solid rgba(0,3,63,0.05)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default MetersComparison;
